import { Box } from '@mui/material';
import { styled as muiStyled } from '@mui/material/styles';
import type { DefaultTheme } from 'styled-components/macro';
import styled from 'styled-components/macro';

import Button from 'components/Button/Button';
import FlexContainer from 'components/FlexContainer/FlexContainer';
import { CloseButtonIconStyled } from 'components/Popup/Popup.styled';
import Typography from 'components/Typography/Typography';

type SectionBoxStyledT = {
  isMobile: boolean;
};

/** Content area fills the modal; white background to match ticket view cards.
 * minHeight: 0 allows flex children to constrain so scrollbar appears when categories overflow. */
export const getPopupContentStyle = (
  theme: DefaultTheme,
  _isMobile: boolean,
  _isTablet: boolean,
  _isDesktop: boolean,
  _isLargeDesktop: boolean
): Record<string, string> => ({
  width: '100%',
  minHeight: '0',
  display: 'flex',
  flexDirection: 'column',
  padding: '0',
  backgroundColor: theme.colors.surface,
});

/** Outer wrapper: fills modal, column layout, scrollable body. */
export const NewTicketPopupContentWrapStyled = styled(FlexContainer)`
  width: 100%;
  height: 100%;
  min-height: 0;
  flex-direction: column;
  align-items: stretch;
`;

/** Scrollable form area: takes remaining space, scrolls when needed. */
export const NewTicketPopupScrollWrapStyled = styled.div`
  flex: 1 1 0;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  width: 100%;
  padding: 1rem 1.5rem 2rem;
  box-sizing: border-box;
`;

export const FormStyled = styled.form`
  display: flex;
  flex-direction: column;
  flex: 1 1 0;
  min-height: 0;
  width: 100%;
  max-width: 100%;
  min-width: 0;
`;

export const FormElementWrapStyled = styled.div`
  width: 100%;
  margin-bottom: 1rem;
`;

export const FormRowWrapStyled = styled(FlexContainer)`
  flex-direction: column;
  ${({ theme }): string => `
    ${theme.devices.tablet} {
      flex-direction: row;
      align-items: flex-start;
      gap: 1rem;
    }
  `}
`;

export const TypographyTitleStyled = styled(Typography)`
  margin-bottom: 1.875rem;
`;

export const FlexContainerMessageStyled = styled(FlexContainer)`
  ${({ theme }): string => `
    ${theme.devices.tablet} {
      padding: 0 2.5rem 0.625rem;
    }
  `}
`;

export const FlexContainerGappedStyled = styled(FlexContainer)`
  position: relative;
  gap: 0.5rem;
  margin-bottom: 0.625rem;
`;

export const FlexContainerCategoryRowStyled = styled(FlexContainerGappedStyled)`
  margin-bottom: -10px; //to compenstate inputs indents
  & > div {
    flex: 1 1 60%;
  }
`;

export const ActionBtnStyled = styled(Button)`
  height: 48px;
  flex: 1 1;
  font-size: ${({ theme }): string => theme.fontSize['2xl']};

  &:first-child {
    margin-right: 1rem;
  }

  ${({ theme }): string => `
    ${theme.devices.tablet} {
      height: 38px;
      font-size: ${theme.fontSize.lg};
    }
  `}
`;

export const AddCategoryLineBtnStyled = styled(Button)`
  padding: 0.5rem;
  margin: 0 0 1rem;
  height: 24px;

  span {
    margin-right: 0;
  }

  svg,
  svg path {
    fill: ${({ theme }): string => theme.colors.primary.variant16};
  }
`;

export const CloseButtonStyled = styled(CloseButtonIconStyled)`
  top: 32px;
  right: -24px;
`;
export const CtaContainerStyled = styled(FlexContainer)`
  margin-top: 1rem;
  ${({ theme }): string => `
    ${theme.devices.tablet} {
      margin-top: 2rem;
    }
  `}
`;

export const SectionTitleStyled = styled(Typography)`
  font-weight: 700;
  font-size: 18px;
  padding-top: 10px;
`;

export const SectionBoxStyled = muiStyled(Box, {
  shouldForwardProp: prop => prop !== 'isMobile',
})<SectionBoxStyledT>(({ theme, isMobile }) => ({
  display: 'flex',
  gap: theme.spacing(4),
  flexDirection: isMobile ? 'column' : 'row',
  width: '100%',
  minWidth: 0,
}));

export const SectionTitleBoxStyled = muiStyled(Box, {
  shouldForwardProp: prop => prop !== 'isMobile',
})<SectionBoxStyledT>(({ isMobile }) => ({
  width: isMobile ? '100%' : '20%',
  justifyItems: isMobile ? 'center' : 'start',
}));

export const SectionRowBoxStyled = muiStyled(Box, {
  shouldForwardProp: prop => prop !== 'isMobile',
})<SectionBoxStyledT>(({ theme, isMobile }) => ({
  display: 'flex',
  gap: theme.spacing(4),
  width: '100%',
  minWidth: 0,
  flexDirection: isMobile ? 'column' : 'row',
  marginTop: '2px',
  marginBottom: '2px',
  flexWrap: 'wrap',
}));

export const SectionSelectBoxStyled = muiStyled(Box, {
  shouldForwardProp: prop => prop !== 'isMobile',
})<SectionBoxStyledT>(({ isMobile }) => ({
  flex: `${isMobile ? '1' : '0'} 1 40%`,
  minWidth: 0,
}));

export const SectionComponentBoxStyled = muiStyled(Box)(() => ({
  flex: '1 1 40%',
  minWidth: 0,
}));

export const SectionToggleBoxStyled = muiStyled(Box, {
  shouldForwardProp: prop => prop !== 'isMobile',
})<SectionBoxStyledT>(({ isMobile }) => ({
  display: 'flex',
  gap: '0.5rem',
  alignItems: 'center',
  marginTop: isMobile ? '0' : '25px',
}));
